<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import type { CustomCategory } from '../../../types/customCategory';
import { getCategoryRoute } from '@shopware-pwa/helpers-next';

const { getVendor, getShippingCosts, getVendorProducts, getFirstChildrenUrl } = useVendor();
const shippingCostsAccordion: Ref<HTMLElement | null> = ref(null);
const openItemsShipping = ref('vendor-shipping-costs');
const returnAndRefundAccordion: Ref<HTMLElement | null> = ref(null);
const openReturnAndRefund = ref('');

const scrollToAccordion = (scrollTo: 'shippingCosts' | 'returnAndRefund') => {
    if (scrollTo === 'shippingCosts') {
        shippingCostsAccordion.value?.scrollIntoView({ behavior: 'smooth' });
        openItemsShipping.value = 'vendor-shipping-costs';
        return;
    }

    returnAndRefundAccordion.value?.scrollIntoView({ behavior: 'smooth' });
    openReturnAndRefund.value = 'vendor-return-refund';
};

const category = inject('category') as Ref<CustomCategory> | undefined;
const vendor = await getVendor(category);
const { shippingCosts, minimumOrderValue } = getShippingCosts(vendor);
const products = await getVendorProducts(vendor?.products);
const productCategory = await getFirstChildrenUrl(category?.value.id);
</script>

<template>
    <div v-if="vendor">
        <!-- general information -->
        
<FormKitLazyProvider config-file="true">
<h1 class="mb-4 mt-10 text-3xl font-bold leading-8 md:mb-6 md:text-4xl">
            {{ vendor.translated.name }}
        </h1>

        <div class="gap-8 md:grid md:grid-cols-2">
            <div class="space-y-6">
                <!-- v-html is necessary because the text contains inline stylings -->
                <!-- eslint-disable vue/no-v-html -->
                <p
                    v-if="vendor.translated.description"
                    v-html="vendor.translated.description"
                />

                <!-- contact information -->
                <div v-if="vendor.translated.address">
                    <h5 class="mb-2 font-bold">
                        {{ $t('vendor.contactInformation') }}
                    </h5>
                    <!-- v-html is necessary because the text contains inline stylings -->
                    <!-- eslint-disable vue/no-v-html -->
                    <p v-html="vendor.translated.address" />
                </div>
            </div>

            <!-- shipping information -->
            <div
                v-if="vendor.shippingMethodId || minimumOrderValue"
                class="mt-6 bg-neutral-300 p-4 md:mt-0 md:flex md:h-max md:items-center md:gap-6 md:px-6 md:py-5"
            >
                <div class="md:bg-white hidden md:block md:shrink-0 md:rounded-full md:p-8">
                    <FormKitIcon
                        icon="truck-fast"
                        class="block h-8 w-8"
                    />
                </div>

                <div class="space-y-1">
                    <div v-if="minimumOrderValue">
                        {{ $t('vendor.minimumOrderValue') }}:
                        <span class="font-bold">
                            {{ minimumOrderValue }}
                        </span>
                    </div>

                    <template v-if="shippingCosts">
                        <div>
                            {{ $t('vendor.shippingCosts') }}:
                            <span class="font-bold">
                                {{ shippingCosts }}
                            </span>
                        </div>

                        <div>
                            {{ $t('vendor.deliveryTime') }}:
                            <span class="font-bold">
                                {{ vendor?.shippingMethod?.deliveryTime?.name }}
                            </span>
                        </div>

                        <div
                            v-if="vendor.translated.shippingInformation"
                            class="cursor-pointer underline"
                            @click="scrollToAccordion('shippingCosts')"
                        >
                            {{ $t('vendor.shippingCosts') }}
                        </div>
                        <div
                            v-if="vendor.translated.returnAndRefundConditions"
                            class="cursor-pointer underline"
                            @click="scrollToAccordion('returnAndRefund')"
                        >
                            {{ $t('vendor.returnRefundConditions') }}
                        </div>
                    </template>
                </div>
            </div>
        </div>

        <!-- topseller products -->
        <div
            v-if="products?.length"
            class="mt-14"
        >
            <div class="md:flex md:justify-between">
                <h3 class="text-xl font-bold">
                    {{ $t('vendor.topsellerProducts', { vendor: vendor.translated.name }) }}
                </h3>

                <!-- we link to the first subcategories (should include all products) -->
                <LocaleLink
                    v-if="productCategory"
                    :to="getCategoryRoute(productCategory)"
                    class="mb-4 underline"
                >
                    {{ $t('vendor.seeMoreTopsellerProducts') }}
                </LocaleLink>
            </div>

            <LayoutSlider
                class="w-full"
                classes="-mx-6 mt-4"
                :class="{
                    'cursor-grab': products.length > 1,
                }"
                :navigation-arrows="false"
                :space-between="32"
                :slides-per-view="1.4"
                :breakpoints="{
                    540: {
                        slidesPerView: 2,
                    },
                    768: {
                        slidesPerView: 3,
                    },
                    1024: {
                        slidesPerView: 4,
                    },
                }"
            >
                <LayoutSliderSlide
                    v-for="product in products"
                    :key="product.id"
                >
                    <ProductCard
                        :product="product"
                        displayMode="contain"
                        layout="minimal"
                        class="max-w-[unset]"
                    />
                </LayoutSliderSlide>
            </LayoutSlider>
        </div>
        <UtilityStaticNotification
            v-else
            type="info"
            :message="$t('cms.element.product.noProductsFound')"
            class="mt-4"
        />

        <!-- accordions -->
        <div class="mt-14 space-y-4">
            <div
                v-if="vendor.translated.shippingInformation"
                ref="shippingCostsAccordion"
            >
                <SharedAccordionRoot v-model="openItemsShipping">
                    <SharedAccordionItem value="vendor-shipping-costs">
                        <template #title>
                            {{ $t('vendor.shippingInformation') }}
                        </template>

                        <template #content>
                            <p v-html="vendor.translated.shippingInformation" />
                        </template>
                    </SharedAccordionItem>
                </SharedAccordionRoot>
            </div>

            <div
                v-if="vendor.translated.returnAndRefundConditions"
                ref="returnAndRefundAccordion"
            >
                <SharedAccordionRoot v-model="openReturnAndRefund">
                    <SharedAccordionItem value="vendor-return-refund">
                        <template #title>
                            {{ $t('vendor.returnRefundConditions') }}
                        </template>

                        <template #content>
                            <p v-html="vendor.translated.returnAndRefundConditions" />
                        </template>
                    </SharedAccordionItem>
                </SharedAccordionRoot>
            </div>

            <SharedAccordionRoot v-if="vendor.translated.termsAndConditions">
                <SharedAccordionItem value="vendor-terms-conditions">
                    <template #title>
                        {{ $t('vendor.termsAndConditions') }}
                    </template>

                    <template #content>
                        <p v-html="vendor.translated.termsAndConditions" />
                    </template>
                </SharedAccordionItem>
            </SharedAccordionRoot>

            <SharedAccordionRoot v-if="vendor.translated.imprint">
                <SharedAccordionItem value="vendor-imprint">
                    <template #title>
                        {{ $t('vendor.imprintAndMore') }}
                    </template>

                    <template #content>
                        <p v-html="vendor.translated.imprint" />
                    </template>
                </SharedAccordionItem>
            </SharedAccordionRoot>
        </div>
</FormKitLazyProvider>

    </div>
</template>

<style scoped>
swiper-container > swiper-slide:last-child {
    @apply pr-6;
}

swiper-container > swiper-slide:first-child {
    @apply pl-6;
}
</style>
